/* eslint-disable arrow-body-style */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { notification } from 'tea-component';
import { LANGUAGE_EN } from '@home/_config/config';
import { copyToClipboard } from '@/pages/aigc/utils/utils';
import { deleteHistory } from '../../../service/service';
import { useSessionId } from '../../hooks/useAgentModalSata';
import { useEditMentionSession, type IEditQuestionFrontendMeta } from './hooks';

interface IUseEditHooksProps {
  question: string;
  userInfo: any;
  setEditObj: any;
  editObj: any;
  messageId: any;
  conversations: any;
  handleSubmit: any;
  setHaveEdit?: (value: boolean) => void;
  frontend_metadata?: IEditQuestionFrontendMeta;
}

const useEditHooks = (props: IUseEditHooksProps) => {
  const {
    question,
    userInfo,
    setEditObj,
    editObj,
    messageId,
    conversations,
    handleSubmit,
    setHaveEdit,
    frontend_metadata: frontendMetadata,
  } = props;

  const [isEdit, setIsEdit] = useState(false);
  const [, setHover] = useState(false);
  const [editSkills, setEditSkills] = useState(
    frontendMetadata?.queryTabs || [],
  );

  const { formatMessage, locale } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  const { sessionId: routeSessionId } = useSessionId();
  const { openid } = userInfo || {};
  const { messageId: editMessageId } = editObj;

  const {
    sessionKey: editSessionKey,
    hydrateEpoch: editHydrateEpoch,
    inputValue,
    handleInputChange,
    editKnowledge,
    setEditKnowledge,
    beginSession,
  } = useEditMentionSession({
    isEdit,
    question,
    frontend_metadata: frontendMetadata,
    isEN,
  });

  const handelEdit = useCallback(() => {
    setHover(false);
    setIsEdit(true);
    setEditSkills(frontendMetadata?.queryTabs || []);
    beginSession();
    setEditObj({
      messageId,
      question,
    });
  }, [messageId, question, setEditObj, frontendMetadata, beginSession]);

  const isShowEditInput = useMemo(() => {
    return isEdit && editMessageId === messageId;
  }, [isEdit, editMessageId, messageId]);

  useEffect(() => {
    setHaveEdit?.(isShowEditInput);
  }, [isShowEditInput, setHaveEdit]);

  const handleEditConfirm = async (
    value: any,
    callBack: any,
    singleMessageFileList: any,
  ) => {
    const targetIndex = conversations?.findIndex(
      (item: { messageId: string; }) => item?.messageId === messageId,
    );
    const targetMessage = targetIndex !== -1 ? conversations[targetIndex] : null;
    const messageMode = targetMessage?.mode;
    const messageModelId = targetMessage?.modeId;
    const deleteMessageIds = conversations
      ?.slice(targetIndex)
      ?.map((conv: { messageId: string; }) => conv.messageId);

    try {
      await deleteHistory({
        sessionId: routeSessionId,
        openId: openid,
        messageIds: deleteMessageIds,
      });
      handleSubmit({
        value,
        fileList: singleMessageFileList,
        options: {
          onSuccess: callBack,
        },
        from: 'edit',
        deleteMessageIds,
        serverProps: {
          mode: messageMode,
          model_id: messageModelId,
          frontend_metadata: targetMessage?.frontend_metadata,
          ...(targetMessage?.metadata || {}),
        },
        mentionConfig: {
          selectedSkills: editSkills,
          selectedKnowledge: editKnowledge,
        },
      });
      setIsEdit(false);
    } catch (error) {
      notification.error({
        description: formatMessage({ id: 'dba_common_edit_failed' }),
        duration: 2000,
      });
    }
  };

  const handleCopy = useCallback(
    async (isOnCopilot: boolean) => {
      const copyState = await copyToClipboard(question);

      if (isOnCopilot) return copyState;

      setHover(false);
      if (copyState) {
        notification.success({
          description: formatMessage({ id: 'dba_common_copy_success' }),
          duration: 2000,
        });
      } else {
        notification.error({
          description: formatMessage({ id: 'dba_common_copy_failed' }),
          duration: 2000,
        });
      }

      return false;
    },
    [question, formatMessage],
  );

  return {
    setIsEdit,
    inputValue,
    handelEdit,
    isShowEditInput,
    editSessionKey,
    editHydrateEpoch,
    handleOnChange: handleInputChange,
    handleEditConfirm,
    handleCopy,
    editSkills,
    setEditSkills,
    editKnowledge,
    setEditKnowledge,
  };
};

export default useEditHooks;
